import axios from 'axios';
import { tokenStorage } from '../utils/auth';
import { getBackendUrl } from '../utils/https';
import { convertHeicToJpeg, isHeicFile, isValidImageFile } from '../utils/heicConverter';

export interface ExtractedData {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  professionalEmail?: string;
  personalEmail?: string;
  professionalPhone?: string;
  personalPhone?: string;
  role?: string;
  department?: string;
  organization?: string;
  location?: string;
  linkedinUrl?: string;
  twitterUrl?: string;
  facebookUrl?: string;
  telegramUrl?: string;
  whatsapp?: string;
  website?: string;
  confidence: {
    overall: number;
    fields: Record<string, number>;
  };
}

export interface OrganizationMatch {
  id: string;
  name: string;
  type: string;
  confidence: number;
  matchType: 'exact' | 'fuzzy' | 'partial';
  /** The other known name the card matched, when it was not the record's own */
  matchedOn?: string;
}

export interface StakeholderMatch {
  id: string;
  firstName: string;
  lastName: string;
  profileImageId?: string;
  score: number;
  matchReasons: string[];
  existingData: {
    firstName: string;
    lastName: string;
    professionalEmail?: string;
    personalEmail?: string;
    professionalPhone?: string;
    personalPhone?: string;
    location?: string;
    bio?: string;
    facebookUrl?: string;
    linkedinUrl?: string;
    twitterUrl?: string;
    telegramUrl?: string;
    whatsapp?: string;
    category?: string;
    profileImageId?: string;
  };
}

class BusinessCardService {
  private baseURL = `${getBackendUrl()}/api`;

  private getAuthHeaders() {
    const token = tokenStorage.get();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async extractFromImage(imageFile: File): Promise<{ extractedData: ExtractedData; businessCardImageId: string }> {
    try {
      // Validate file type
      if (!isValidImageFile(imageFile)) {
        throw new Error('Invalid image type. Only JPEG, PNG, WebP, and HEIC are supported.');
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024;
      if (imageFile.size > maxSize) {
        throw new Error(
          `Image is too large (${(imageFile.size / 1024 / 1024).toFixed(1)}MB). Maximum size is 10MB.`
        );
      }

      // Convert HEIC to JPEG if needed
      let processedFile = imageFile;
      if (isHeicFile(imageFile)) {
        processedFile = await convertHeicToJpeg(imageFile);
      }

      const formData = new FormData();
      formData.append('image', processedFile);

      const response = await axios.post(`${this.baseURL}/business-card/upload`, formData, {
        headers: {
          ...this.getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // 60 second timeout for AI extraction
      });

      return response.data as any;
    } catch (error: any) {

      // Extract meaningful error message from the response
      let errorMessage = 'Failed to extract data from business card. Please try again.';

      // Check for NestJS error format first
      if (error.response?.data?.message) {
        errorMessage = String(error.response.data.message);
      } else if (error.response?.data?.error) {
        // Handle both string and object error formats
        if (typeof error.response.data.error === 'string') {
          errorMessage = String(error.response.data.error);
        } else if (error.response.data.error?.message) {
          errorMessage = String(error.response.data.error.message);
        } else {
          errorMessage = String(error.response.data.error);
        }
      } else if (error.message) {
        errorMessage = String(error.message);
      } else if (error.response?.statusText) {
        errorMessage = String(error.response.statusText);
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      throw new Error(errorMessage);
    }
  }

  async matchOrganization(organizationName: string): Promise<OrganizationMatch[]> {
    try {
      const response = await axios.get(`${this.baseURL}/business-card/match-organization`, {
        params: { name: organizationName },
        headers: this.getAuthHeaders(),
      });

      return response.data as any;
    } catch (error: any) {

      // Extract meaningful error message from the response
      let errorMessage = 'Failed to match organization. Please try again.';

      if (error.response?.data?.message) {
        errorMessage = String(error.response.data.message);
      } else if (error.response?.data?.error) {
        errorMessage = String(error.response.data.error);
      } else if (error.message) {
        errorMessage = String(error.message);
      } else if (error.response?.statusText) {
        errorMessage = String(error.response.statusText);
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      throw new Error(errorMessage);
    }
  }

  async matchStakeholder(data: {
    firstName?: string;
    lastName?: string;
    professionalEmail?: string;
    personalEmail?: string;
    professionalPhone?: string;
    personalPhone?: string;
  }): Promise<StakeholderMatch[]> {
    try {
      const response = await axios.get(`${this.baseURL}/business-card/match-stakeholder`, {
        params: data,
        headers: this.getAuthHeaders(),
      });

      return response.data as any;
    } catch (error: any) {
      // Non-blocking: return empty array on failure so creation can proceed
      console.warn('Stakeholder match check failed:', error.message);
      return [];
    }
  }

  async createOrganization(organizationName: string, organizationType: string): Promise<{ id: string; name: string; type: string; status: string }> {
    try {
      const response = await axios.post(`${this.baseURL}/business-card/create-organization`, {
        organizationName,
        organizationType,
      }, {
        headers: this.getAuthHeaders(),
      });

      return response.data as any;
    } catch (error: any) {

      // Extract meaningful error message from the response
      let errorMessage = 'Failed to create organization. Please try again.';

      if (error.response?.data?.message) {
        errorMessage = String(error.response.data.message);
      } else if (error.response?.data?.error) {
        errorMessage = String(error.response.data.error);
      } else if (error.message) {
        errorMessage = String(error.message);
      } else if (error.response?.statusText) {
        errorMessage = String(error.response.statusText);
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      throw new Error(errorMessage);
    }
  }
}

export const businessCardService = new BusinessCardService();
